import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../../../context/AuthContext";
import { oneKlicks } from "../../../SDK/oneclick";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { Form, InputGroup, Badge } from "react-bootstrap";
import { Button, Select } from "antd";
import { PlusOutlined, EyeOutlined } from "@ant-design/icons";

const parseMetaPayload = (metaValue) => {
    if (!metaValue) {
        return {};
    }
    if (typeof metaValue === "object") {
        return metaValue || {};
    }
    try {
        return JSON.parse(metaValue);
    } catch (error) {
        console.warn("Unable to parse meta payload", error);
        return {};
    }
};

const toNumericOrNull = (value) => {
    if (value === undefined || value === null || value === "") {
        return null;
    }
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
};

const calculateMonthlyPremium = (annualValue) => {
    const annual = Number(annualValue);
    if (!Number.isFinite(annual) || annual <= 0) {
        return 0;
    }
    return Math.round((annual / 12) * 100) / 100;
};

const recordMatchesInsuranceUser = (record, userData) => {
    if (!record || !userData?.id) {
        return false;
    }

    const userId = toNumericOrNull(userData.id);
    const companyId = toNumericOrNull(userData.company_id || userData.companyId || userData.company);
    const agentId = toNumericOrNull(userData.agent_id || userData.agentId);

    const recordCompanyId = toNumericOrNull(record.company_id || record.companyId);
    const recordAgentId = toNumericOrNull(record.agent_id || record.agentId);

    if (companyId && recordCompanyId && companyId === recordCompanyId) {
        return true;
    }

    if (agentId && recordAgentId && agentId === recordAgentId) {
        return true;
    }

    const directUserCandidates = [
        record.user_id,
        record.userId,
        record.created_by,
        record.createdBy,
        record.owner_id,
        record.ownerId,
        record.agent_user_id,
    ]
        .map(toNumericOrNull)
        .filter((value) => value !== null);

    if (directUserCandidates.includes(userId)) {
        return true;
    }

    const meta = parseMetaPayload(record.meta);
    const metaCandidates = [
        meta?.created_by,
        meta?.user_id,
        meta?.owner_id,
        meta?.agent_id,
    ]
        .map(toNumericOrNull)
        .filter((value) => value !== null);

    return metaCandidates.includes(userId);
};

const Policies = () => {
    const { userData } = useAuth();
    const navigate = useNavigate();
    const [policies, setPolicies] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState("all"); // all, active, expired, pending_renewal
    const [searchText, setSearchText] = useState("");

    const companyId = useMemo(
        () => toNumericOrNull(userData?.company_id || userData?.companyId || userData?.company),
        [userData]
    );

    const agentId = useMemo(
        () => toNumericOrNull(userData?.agent_id || userData?.agentId),
        [userData]
    );

    useEffect(() => {
        fetchPolicies();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId, agentId, filter, userData?.id]);

    const fetchPolicies = async () => {
        if (!userData?.id) {
            setPolicies([]);
            return;
        }

        try {
            setLoading(true);
            const conditions = {};
            if (filter !== "all") {
                conditions.status = filter;
            }
            if (companyId) {
                conditions.company_id = companyId;
            }
            if (!conditions.company_id && agentId) {
                conditions.agent_id = agentId;
            }
            if (!conditions.company_id && !conditions.agent_id) {
                conditions.user_id = userData.id;
            }

            const response = await oneKlicks.read(
                "",
                "insurance_policies",
                conditions,
                "created_at DESC",
                "",
                "",
                "0,200"
            );

            if (response?.success) {
                const rows = Array.isArray(response?.data) ? response.data : Array.isArray(response?.data?.items) ? response.data.items : [];
                const filteredPolicies = rows.filter((policy) => recordMatchesInsuranceUser(policy, userData));

                const normalised = filteredPolicies.map((policy) => ({
                    id: policy.id,
                    policy_number: policy.policy_number || `POL-${policy.id}`,
                    policy_holder_name: policy.policy_holder_name || policy.customer_name || "N/A",
                    coverage_type: policy.coverage_type || "N/A",
                    premium_amount: policy.premium_amount ?? 0,
                    monthly_premium: calculateMonthlyPremium(policy.premium_amount),
                    status: policy.status || "active",
                    created_at: policy.created_at,
                }));
                setPolicies(normalised);
            } else {
                setPolicies([]);
            }
        } catch (error) {
            console.error("Error fetching policies:", error);
            toast.error("Failed to load policies");
            setPolicies([]);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        if (!amount) {
            return "₹0";
        }
        return new Intl.NumberFormat("en-IN", {
            style: "currency",
            currency: "INR",
            maximumFractionDigits: 0,
        }).format(amount);
    };

    const filteredPolicies = useMemo(() => {
        if (!searchText.trim()) {
            return policies;
        }
        const searchLower = searchText.toLowerCase();
        return policies.filter((policy) => {
            return (
                policy.policy_number?.toLowerCase().includes(searchLower) ||
                policy.policy_holder_name?.toLowerCase().includes(searchLower) ||
                policy.coverage_type?.toLowerCase().includes(searchLower) ||
                policy.status?.toLowerCase().includes(searchLower)
            );
        });
    }, [policies, searchText]);

    const displayPolicies = filter === "all" ? filteredPolicies : filteredPolicies.filter((p) => p.status === filter);

    return (
        <div className="subbody" style={{ width: '100%', padding: '2% 4%' }}>
            <div className="dc-haslayout dc-dbsectionspace">
                <div className="container-fluid py-3">
                    <div className="card mb-5" style={{ borderRadius: '5px' }}>
                        <div className="card-body" style={{ padding: '24px 30px', borderRadius: '5px' }}>
                            <div className="d-flex align-items-center justify-content-between mb-4">
                                <div className="d-flex align-items-center gap-2">
                                    <h5 className="mb-0 font-weight-bold">Policies Management</h5>
                                    <Badge bg="info" style={{ borderRadius: '5px' }}>Policies</Badge>
                                </div>
                                <Button className="btn-primary" onClick={() => navigate("/insurance/new-policy")} style={{ height: '40px', borderRadius: '5px' }}>
                                    <PlusOutlined /> Add New Policy
                                </Button>
                            </div>
                            <div className="d-flex flex-wrap align-items-center gap-3" style={{ marginBottom: '20px' }}>
                                <div style={{ minWidth: '200px', flex: '0 0 auto' }}>
                                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: '#495057' }}>
                                        Filter by Status
                                    </label>
                                    <Select
                                        value={filter}
                                        onChange={setFilter}
                                        style={{ width: '100%', borderRadius: '5px' }}
                                        size="large"
                                        options={[
                                            { value: 'all', label: 'All Policies' },
                                            { value: 'active', label: 'Active' },
                                            { value: 'expired', label: 'Expired' },
                                            { value: 'pending_renewal', label: 'Pending Renewal' },
                                        ]}
                                    />
                                </div>
                                <div style={{ flex: '1', minWidth: '250px', maxWidth: '400px' }}>
                                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: '#495057' }}>
                                        Search
                                    </label>
                                    <InputGroup>
                                        <Form.Control
                                            placeholder="Search by policy number, holder name, coverage type..."
                                            value={searchText}
                                            onChange={(e) => setSearchText(e.target.value)}
                                            style={{ height: '40px', borderRadius: '5px' }}
                                        />
                                    </InputGroup>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="card" style={{ borderRadius: '5px' }}>
                        <div className="card-body" style={{ padding: '24px 30px', borderRadius: '5px' }}>
                            {loading ? (
                                <div className="text-center p-5">
                                    <div className="spinner-border" role="status">
                                        <span className="sr-only">Loading...</span>
                                    </div>
                                </div>
                            ) : displayPolicies.length > 0 ? (
                                <div className="table-responsive">
                                    <table hover size="sm" className="mb-0 align-middle">
                                        <thead>
                                            <tr>
                                                <th>Policy Number</th>
                                                <th>Policy Holder</th>
                                                <th>Coverage Type</th>
                                                <th>Annual Premium</th>
                                                <th>Monthly Premium</th>
                                                <th>Status</th>
                                                <th style={{ width: '150px' }}>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {displayPolicies.map((policy) => (
                                                <tr key={policy.id}>
                                                    <td data-label="Policy Number">{policy.policy_number || `POL-${policy.id}`}</td>
                                                    <td data-label="Policy Holder">{policy.policy_holder_name || "N/A"}</td>
                                                    <td data-label="Coverage Type">{policy.coverage_type || "N/A"}</td>
                                                    <td data-label="Annual Premium">{formatCurrency(policy.premium_amount)}</td>
                                                    <td data-label="Monthly Premium">{formatCurrency(policy.monthly_premium)}</td>
                                                    <td data-label="Status" style={{ textAlign: 'center' }}>
                                                        <span className={`badge ${policy.status === "active" ? "badge-success" : policy.status === "expired" ? "badge-danger" : "badge-warning"}`} style={{ borderRadius: '5px' }}>
                                                            {policy.status || "N/A"}
                                                        </span>
                                                    </td>
                                                    <td data-label="Action">
                                                        <div style={{ display: 'flex', flexDirection: 'row', gap: '6px', alignItems: 'center', flexWrap: 'nowrap', justifyContent: 'flex-end' }}>
                                                            <Button
                                                                href="#"
                                                                className="dc-update-servicesss fit-content btn-info"
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    navigate(`/insurance/policies/${policy.id}`);
                                                                }}
                                                                style={{ borderRadius: '5px' }}
                                                            >
                                                                <EyeOutlined style={{ fontSize: '11px', marginRight: '4px' }} />
                                                                View Details
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="dc-emptydata-holder">
                                    <div className="dc-emptydata">
                                        <div className="dc-emptydetails dc-empty-booking dc-emptyholder-sm">
                                            <span></span>
                                            <em>No policies found</em>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Policies;

